import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import Spinner from '@/components/Spinner.tsx';
import { showToast } from '@/components/Toast.tsx';
import { useEntityConfig } from '@/hooks/useEntityConfig.tsx';
import axios from '@/lib/axios.ts';
import { isAllowedOAuthReturnUrl, storeOAuthReturnTo } from '@/utils/embedAuth.ts';

const B2CoreOAuthStart = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const config = useEntityConfig();
  const started = useRef(false);

  useEffect(() => {
    if (started.current) {
      return;
    }

    started.current = true;

    const returnTo = searchParams.get('return_to');

    if (
      returnTo &&
      isAllowedOAuthReturnUrl(returnTo, [config.VITE_TRADING_VIEW_URL, config.VITE_TRADING_VIEW_REPLAY_URL])
    ) {
      storeOAuthReturnTo(returnTo);
    }

    const startOAuth = async () => {
      try {
        const response = await axios.get<{ url: string }>('/oauth/b2core/login', {
          withCredentials: true,
          headers: { 'X-Trace-ID': uuidv4() },
        });
        window.location.assign(response.data.url);
      } catch {
        showToast({
          type: 'error',
          title: t('error_title'),
          description: t('oauth_login_failed'),
        });
        navigate('/login', { replace: true });
      }
    };

    void startOAuth();
  }, [config.VITE_TRADING_VIEW_REPLAY_URL, config.VITE_TRADING_VIEW_URL, navigate, searchParams, t]);

  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        window.location.replace('/login');
      }
    };
    window.addEventListener('pageshow', onPageShow);

    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  return <Spinner />;
};

export default B2CoreOAuthStart;
